// Performance (TWR) — time-weighted return for the strategy book.
//
// Phase 1 of the Performance-tab expansion (Plans/performance_and_risk_tabs.md):
//   • Multi-account SUBSET consolidation (pick any set of IBKR accounts).
//   • Range presets (MTD/1M/3M/YTD/1Y/ALL) + a custom from–to date window — all a
//     pure client-side rebase of the one inception→today TWR series.
//   • Window-aware KPI tiles (return/ann/vol/Sharpe/MaxDD recomputed for the window).
//   • Per-strategy·per-epoch table: sortable, top-N + expand, AUM as-of end + weight.
// Deeper scope (basket/strategy drill), attribution, and the Risk tab are later phases.
import React, { useEffect, useMemo, useState } from 'react';
import { tradingService } from '../../services/tradingService';
import useSharedAccount from './portfolio/useSharedAccount';
import MiniTwrChart from './portfolio/MiniTwrChart';
import CommissionScenarioPanel from './portfolio/CommissionScenarioPanel';
import DividendsPanel from './portfolio/DividendsPanel';
import LookthroughPanel from './portfolio/LookthroughPanel';
import AttributionPanel from './portfolio/AttributionPanel';
import { MultiPicker, GroupedPicker } from './portfolio/pickers';
import { RANGES, RANGE_LABELS, rebaseSeries, rebaseCustom, seriesStats } from './portfolio/twrRange';

const pct = (v, dp = 2) => (v == null || isNaN(v) ? '—' : `${(v * 100).toFixed(dp)}%`);
const signClass = (v) => (v == null || isNaN(v) ? 'text-gray-400' : v >= 0 ? 'text-emerald-500' : 'text-red-500');
const fmtAum = (v) => (v == null || isNaN(v) ? '—' : `$${Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`);

const Tile = ({ label, value, colored, bench, benchLabel = 'SPX' }) => (
  <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-3">
    <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">{label}</div>
    <div className={`text-lg font-semibold tabular-nums ${colored !== undefined ? signClass(colored) : 'text-gray-800 dark:text-gray-200'}`}>{value}</div>
    {bench !== undefined && (
      <div className="flex items-center gap-1 text-[10px] text-gray-400 mt-0.5">
        <span className="inline-block w-2 border-t border-dashed" style={{ borderColor: '#d4696f' }} />
        <span>{benchLabel} {bench}</span>
      </div>
    )}
  </div>
);

// ── sortable per-strategy·per-epoch table ──────────────────────────────────────
const EPOCH_COLS = [
  { key: 'strategy', label: 'Strategy', align: 'left' },
  { key: 'epoch', label: 'Epoch', align: 'right' },
  { key: 'aum_end', label: 'AUM', align: 'right' },
  { key: 'weight_pct', label: 'Weight', align: 'right' },
  { key: 'twr', label: 'TWR', align: 'right' },
  { key: 'ann_return', label: 'Ann.', align: 'right' },
  { key: 'vol', label: 'Vol', align: 'right' },
  { key: 'sharpe', label: 'Sharpe', align: 'right' },
  { key: 'max_drawdown', label: 'Max DD', align: 'right' },
];
const TOP_N = 10;

const EpochTable = ({ epochs }) => {
  const [sort, setSort] = useState({ key: 'aum_end', dir: 'desc' });
  const [expanded, setExpanded] = useState(false);
  const sorted = useMemo(() => {
    const arr = [...epochs].sort((a, b) => {
      const va = a[sort.key] ?? (typeof a[sort.key] === 'string' ? '' : -Infinity);
      const vb = b[sort.key] ?? (typeof b[sort.key] === 'string' ? '' : -Infinity);
      const c = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
      return sort.dir === 'asc' ? c : -c;
    });
    return arr;
  }, [epochs, sort]);
  const rows = expanded ? sorted : sorted.slice(0, TOP_N);
  const setCol = (k) => setSort((s) => ({ key: k, dir: s.key === k && s.dir === 'desc' ? 'asc' : 'desc' }));
  const arrow = (k) => (sort.key === k ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : '');

  return (
    <div>
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-[12px] min-w-[640px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-100 dark:border-gray-800">
              {EPOCH_COLS.map((c) => (
                <th key={c.key} onClick={() => setCol(c.key)}
                  className={`py-2 px-3 cursor-pointer select-none hover:text-gray-600 dark:hover:text-gray-200 ${c.align === 'right' ? 'text-right' : 'text-left'} ${sort.key === c.key ? 'text-brand-gold' : ''}`}>
                  {c.label}{arrow(c.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800/60">
            {rows.map((e, i) => (
              <tr key={`${e.strategy}-${e.epoch}-${i}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                <td className="py-1.5 px-3 text-gray-700 dark:text-gray-300 truncate max-w-[220px]" title={e.strategy}>{e.strategy}</td>
                <td className="py-1.5 px-3 text-right tabular-nums text-gray-500">{e.epoch}</td>
                <td className="py-1.5 px-3 text-right tabular-nums text-gray-700 dark:text-gray-300">{fmtAum(e.aum_end)}</td>
                <td className="py-1.5 px-3 text-right tabular-nums text-gray-500">{pct(e.weight_pct, 1)}</td>
                <td className={`py-1.5 px-3 text-right tabular-nums font-medium ${signClass(e.twr)}`}>{pct(e.twr)}</td>
                <td className={`py-1.5 px-3 text-right tabular-nums ${signClass(e.ann_return)}`}>{pct(e.ann_return)}</td>
                <td className="py-1.5 px-3 text-right tabular-nums text-gray-500">{pct(e.vol)}</td>
                <td className={`py-1.5 px-3 text-right tabular-nums ${signClass(e.sharpe)}`}>{e.sharpe == null ? '—' : e.sharpe.toFixed(2)}</td>
                <td className={`py-1.5 px-3 text-right tabular-nums ${signClass(e.max_drawdown)}`}>{pct(e.max_drawdown)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sorted.length > TOP_N && (
        <button onClick={() => setExpanded((x) => !x)} className="mt-2 text-[11px] text-brand-gold hover:underline">
          {expanded ? 'Show top 10' : `Show all ${sorted.length}`}
        </button>
      )}
    </div>
  );
};

// ── page ───────────────────────────────────────────────────────────────────────
const TwrPerformancePage = () => {
  const [accounts, setAccounts] = useState([]);
  const [sharedAccountId, setSharedAccountId] = useSharedAccount();
  const [selectedIds, setSelectedIds] = useState(null); // null until accounts load; [] = all
  const [level, setLevel] = useState('portfolio'); // 'portfolio' | 'basket' | 'strategy'
  const [portfolioIds, setPortfolioIds] = useState([]); // chosen baskets (combine)
  const [strategyIds, setStrategyIds] = useState([]); // chosen strategies
  const [baskets, setBaskets] = useState([]);
  const [allStrategies, setAllStrategies] = useState([]);
  const [range, setRange] = useState('YTD');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Phase B: capital-mode view. '' = persisted default; 'pool' | 'per_deploy' = view-time
  // override (recompute-on-read, non-persisting). Only meaningful at single-account portfolio scope.
  const [modeView, setModeView] = useState('');
  const [soloInfusions, setSoloInfusions] = useState(null); // #infusions on the single selected account
  const [modeBusy, setModeBusy] = useState(false);

  useEffect(() => {
    tradingService.listIBKRAccounts()
      .then((r) => {
        const list = r.data?.results || r.data || [];
        setAccounts(list);
        // Default scope: the shared account (single), else all.
        setSelectedIds(sharedAccountId != null ? [sharedAccountId] : []);
      })
      .catch(() => setAccounts([]));
    tradingService.listPortfolios().then((r) => setBaskets(r.data?.results || r.data || [])).catch(() => {});
    tradingService.getStrategies().then((r) => setAllStrategies(r.data?.results || r.data || [])).catch(() => {});
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  // Follow the shared account when it changes elsewhere (only while single-select).
  useEffect(() => {
    if (selectedIds && selectedIds.length === 1 && sharedAccountId != null && selectedIds[0] !== sharedAccountId) {
      setSelectedIds([sharedAccountId]);
    }
  }, [sharedAccountId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const onScopeChange = (ids) => {
    setSelectedIds(ids);
    // A single concrete pick syncs the shared account (Overview/Orders follow).
    if (ids.length === 1) setSharedAccountId(ids[0]);
  };

  // Strategies available to the Strategy-level picker — filtered to the account scope.
  const scopedStrategies = useMemo(() => {
    const inScope = (s) => !selectedIds || selectedIds.length === 0 || selectedIds.includes(s.account);
    return allStrategies.filter(inScope).map((s) => ({ id: s.id, name: s.name, tag: s.account_is_paper ? 'paper' : 'live' }));
  }, [allStrategies, selectedIds]);

  // Group the in-scope strategies under their basket(s) for the grouped picker;
  // strategies in no basket fall under "Ungrouped".
  const strategyGroups = useMemo(() => {
    const inScope = new Set(scopedStrategies.map((s) => s.id));
    const groups = [];
    const grouped = new Set();
    baskets.forEach((b) => {
      const kids = (b.members || []).filter((m) => inScope.has(m.strategy_id)).map((m) => ({ id: m.strategy_id, name: m.strategy_name }));
      if (kids.length) { groups.push({ key: `b${b.id}`, label: b.name, children: kids }); kids.forEach((k) => grouped.add(k.id)); }
    });
    const ungrouped = scopedStrategies.filter((s) => !grouped.has(s.id)).map((s) => ({ id: s.id, name: s.name }));
    if (ungrouped.length) groups.push({ key: 'ungrouped', label: 'Ungrouped', children: ungrouped });
    return groups;
  }, [baskets, scopedStrategies]);

  // Baskets belonging to the selected account(s): a basket is in-scope if any of its
  // members' strategies sit on a selected account. Without this the picker listed EVERY
  // basket regardless of scope — so the LIVE trend-buyer showed up while viewing the paper
  // account (near-identical name), which is exactly the confusion to kill.
  const scopedBaskets = useMemo(() => {
    if (!selectedIds || selectedIds.length === 0) return baskets;   // no account scope → all
    // Filter by the members' OWN account (serialized on each member), NOT the separate
    // strategy list — that list is paginated/incomplete, which made EVERY basket fall out
    // of scope ("None in scope"). A basket is in scope if any member sits on a picked account.
    const sel = new Set(selectedIds);
    return baskets.filter((b) => (b.members || []).some((m) => sel.has(m.account)));
  }, [baskets, selectedIds]);

  // Drop any picked basket that's no longer on the selected account(s) when scope changes.
  useEffect(() => {
    const ok = new Set(scopedBaskets.map((b) => b.id));
    setPortfolioIds((prev) => (prev.some((id) => !ok.has(id)) ? prev.filter((id) => ok.has(id)) : prev));
  }, [scopedBaskets]);   // eslint-disable-line react-hooks/exhaustive-deps

  const basketEmpty = level === 'basket' && !portfolioIds.length;

  // The single account in scope at portfolio level — the only context where the capital-mode
  // view/selector applies (multi-account / subset curves have no single capital_mode).
  const soloAccountId = (level === 'portfolio' && selectedIds && selectedIds.length === 1) ? selectedIds[0] : null;
  const soloAccount = soloAccountId != null ? accounts.find((a) => a.id === soloAccountId) : null;

  // Clear the view-override whenever the single-account context changes (avoid a stale override
  // leaking onto a different account).
  useEffect(() => { setModeView(''); }, [soloAccountId]);

  // Know whether the solo account has infusions → gate the Pool option (server also enforces).
  useEffect(() => {
    if (soloAccountId == null) { setSoloInfusions(null); return; }
    let alive = true;
    tradingService.listAccountCashFlows(soloAccountId)
      .then((r) => { const d = r.data; if (alive) setSoloInfusions((Array.isArray(d) ? d : (d.results || [])).length); })
      .catch(() => alive && setSoloInfusions(0));
    return () => { alive = false; };
  }, [soloAccountId]);

  // Persist the current view-override as the account's DEFAULT capital_mode (admin-gated on the
  // server; pool refused without infusions). On success, refetch accounts + clear the override.
  const persistMode = async (m) => {
    if (!soloAccount) return;
    setModeBusy(true); setError(null);
    try {
      await tradingService.updateIBKRAccount(soloAccount.id, { capital_mode: m });
      const r = await tradingService.listIBKRAccounts();
      setAccounts(r.data?.results || r.data || []);
      setModeView('');
    } catch (e) {
      const d = e?.response?.data;
      setError((d && (d.capital_mode || d.error)) || 'Could not set capital mode.');
    } finally { setModeBusy(false); }
  };

  useEffect(() => {
    if (selectedIds == null) return;
    // Basket level REQUIRES an explicit basket pick — never silently fall back to the
    // whole-account curve. That fallback showed a month of unrelated pre-inception account
    // history under a "Basket" heading (e.g. a basket deployed 07-21 charting from 06-24).
    if (level === 'basket' && !portfolioIds.length) {
      setData(null); setLoading(false); setError(null); return;
    }
    setLoading(true); setError(null);
    const opts = level === 'basket' && portfolioIds.length ? { portfolioIds }
      : level === 'strategy' && strategyIds.length ? { strategyIds }
      : (soloAccountId != null && modeView) ? { mode: modeView }   // view-time capital-mode override
      : {};
    tradingService.getPortfolioTwr(selectedIds.length ? selectedIds : null, opts)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.error || 'Failed to load TWR'))
      .finally(() => setLoading(false));
  }, [selectedIds, level, portfolioIds, strategyIds, modeView, soloAccountId]);

  const series = data?.series || [];
  const spySeries = data?.spy_series || [];
  const benchLabel = data?.benchmark_label || 'SPX';   // 'SPX' or 'SPX (net of WHT)' — the stored series' flavour
  const isCustom = range === 'CUSTOM';
  const asOf = series.length ? series[series.length - 1].date : null;
  const port = isCustom ? rebaseCustom(series, customStart, customEnd) : rebaseSeries(series, range, asOf);
  const spy = isCustom ? rebaseCustom(spySeries, customStart, customEnd) : rebaseSeries(spySeries, range, asOf);
  const stats = useMemo(() => seriesStats(port.points), [port.points]);
  const spyStats = useMemo(() => seriesStats(spy.points), [spy.points]);
  const hasSpy = spy.points.length > 0;
  const refreshing = loading && !!data; // a scope/range change refetch (data still shown)

  const rangeBtns = [...RANGES, 'CUSTOM'];

  // Download the FULL inception→today daily TWR series as CSV. Two flavours per series:
  //   *_cum_twr  — cumulative TWR from inception (flow-neutral, rebased to 0 at inception).
  //   *_daily_twr — THAT day's return, from consecutive cumulatives: r_t=(1+cum_t)/(1+cum_{t-1})−1.
  // Growth-of-100 externally = 100 × (1 + portfolio_cum_twr). 6 decimals.
  const downloadTwrCsv = () => {
    const s = data?.series || [];
    if (!s.length) return;
    const spyBy = Object.fromEntries((data?.spy_series || []).map((p) => [p.date, p.cumulative]));
    const oldBy = Object.fromEntries((data?.old_series || []).map((p) => [p.date, p.cumulative]));
    const f = (v) => (v == null ? '' : Number(v).toFixed(6));
    const day = (cum, prev) => (cum == null || prev == null) ? null : (1 + cum) / (1 + prev) - 1;
    const lines = ['date,portfolio_cum_twr,portfolio_daily_twr,benchmark_cum_twr,benchmark_daily_twr,money_weighted_cum'];
    let prevP = null, prevB = null, first = true;
    s.forEach((p) => {
      const bCum = spyBy[p.date] ?? null;
      const pDay = first ? 0 : day(p.cumulative, prevP);          // first row = inception seed → 0
      const bDay = first ? (bCum == null ? null : 0) : day(bCum, prevB);
      lines.push([p.date, f(p.cumulative), f(pDay), f(bCum), f(bDay), f(oldBy[p.date])].join(','));
      prevP = p.cumulative; prevB = bCum; first = false;
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const nm = String(data?.account_name || 'portfolio').replace(/[^a-z0-9]+/gi, '_').slice(0, 40);
    const a = document.createElement('a');
    a.href = url;
    a.download = `twr_daily_${nm}_${asOf || 'series'}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-serif text-gray-800 dark:text-gray-100 flex items-center">
            Performance — Time-Weighted Return
            {refreshing && <span className="ml-2 inline-block w-3.5 h-3.5 border-2 border-brand-gold/70 border-t-transparent rounded-full animate-spin" />}
          </h2>
          <p className="text-[11px] text-gray-400">{refreshing ? 'Updating…' : `GIPS daily-valuation TWR · flow-neutral · per-epoch · ${data?.account_name || ''}`}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {accounts.length > 0 && (
            <>
              <span className="text-[10px] uppercase tracking-wider text-gray-400">Accounts</span>
              <MultiPicker items={accounts.map((a) => ({ id: a.id, name: a.name, tag: a.is_paper ? 'paper' : 'live' }))}
                selected={selectedIds} onChange={onScopeChange} allLabel="All accounts (consolidated)" singular="account" />
            </>
          )}
          <button onClick={downloadTwrCsv} disabled={!series.length}
            title={`Download the daily TWR series — portfolio + ${benchLabel} benchmark, both CUMULATIVE and PER-DAY return (6 dp), + money-weighted — as CSV`}
            className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:text-brand-gold hover:border-brand-gold disabled:opacity-40 transition-colors whitespace-nowrap">
            ↓ Daily TWR CSV
          </button>
        </div>
      </div>

      {/* scope drill: level + filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-700 overflow-hidden text-sm">
          {[['portfolio', 'Portfolio'], ['basket', 'Basket'], ['strategy', 'Strategy']].map(([lv, lab]) => (
            <button key={lv} onClick={() => setLevel(lv)}
              className={`px-3 py-1.5 ${level === lv ? 'bg-brand-gold/10 text-brand-gold font-semibold' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>{lab}</button>
          ))}
        </div>
        {level === 'basket' && (
          <MultiPicker
            items={scopedBaskets.map((b) => ({ id: b.id, name: b.name, tag: b.is_active ? 'active' : 'inactive' }))}
            selected={portfolioIds} onChange={setPortfolioIds}
            allLabel="Pick baskets to combine…" singular="basket" width="min-w-[220px]" />
        )}
        {level === 'strategy' && (
          <GroupedPicker groups={strategyGroups} selected={strategyIds} onChange={setStrategyIds}
            allLabel="All strategies in scope" singular="strategy" />
        )}
        {level === 'basket' && portfolioIds.length === 0 && <span className="text-[11px] text-gray-400">tick one or more baskets to combine</span>}
        {level === 'strategy' && strategyIds.length === 0 && <span className="text-[11px] text-gray-400">showing all strategies in scope</span>}
      </div>

      {/* Capital mode (Phase B): only at single-account portfolio scope. Default = persisted
          capital_mode; Pool/Per-deploy override the VIEW (recompute, non-persisting). "Set as
          default" persists the chosen mode. Pool is disabled without infusions. */}
      {soloAccount && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-gray-400">Capital mode</span>
          <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-700 overflow-hidden text-xs">
            {[
              ['', `Default · ${soloAccount.capital_mode === 'pool' ? 'Pool' : 'Per-deploy'}`],
              ['per_deploy', 'Per-deploy'],
              ['pool', 'Pool'],
            ].map(([m, lab]) => {
              const poolDisabled = m === 'pool' && soloInfusions === 0;
              const active = modeView === m;
              return (
                <button key={m || 'default'} disabled={poolDisabled}
                  onClick={() => setModeView(m)}
                  title={poolDisabled ? 'Pool needs an infusion timeline — add infusions in the Infusion Manager' : undefined}
                  className={`px-3 py-1 ${active ? 'bg-brand-gold/10 text-brand-gold font-semibold' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'} ${poolDisabled ? 'opacity-40 cursor-not-allowed' : ''}`}>
                  {lab}
                </button>
              );
            })}
          </div>
          {modeView && modeView !== soloAccount.capital_mode && (
            <button onClick={() => persistMode(modeView)} disabled={modeBusy || (modeView === 'pool' && soloInfusions === 0)}
              className="text-[11px] text-brand-gold hover:underline disabled:opacity-40 disabled:no-underline">
              {modeBusy ? 'Saving…' : 'Set as default'}
            </button>
          )}
          {modeView === 'per_deploy' && soloAccount.capital_mode === 'pool' && (
            <span className="text-[10px] text-amber-500">comparison only — double-counts internal draws</span>
          )}
          {soloInfusions === 0 && (
            <span className="text-[10px] text-gray-400">Pool needs infusions — add them in the Infusion Manager tab</span>
          )}
        </div>
      )}

      {/* range + custom dates */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-700 overflow-hidden text-sm">
          {rangeBtns.map((r) => (
            <button key={r} onClick={() => setRange(r)}
              className={`px-3 py-1 ${range === r ? 'bg-brand-gold/10 text-brand-gold font-semibold' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
              {r === 'CUSTOM' ? 'Custom' : (RANGE_LABELS[r] || r)}
            </button>
          ))}
        </div>
        {isCustom && (
          <div className="flex items-center gap-1.5 text-sm">
            <input type="date" value={customStart} max={customEnd || asOf || undefined} onChange={(e) => setCustomStart(e.target.value)}
              className="bg-white dark:bg-[#161b22] border border-gray-300 dark:border-gray-700 rounded px-2 py-1" />
            <span className="text-gray-400">→</span>
            <input type="date" value={customEnd} min={customStart || undefined} max={asOf || undefined} onChange={(e) => setCustomEnd(e.target.value)}
              className="bg-white dark:bg-[#161b22] border border-gray-300 dark:border-gray-700 rounded px-2 py-1" />
          </div>
        )}
        {port.anchorDate && <span className="text-[11px] text-gray-400">from {port.anchorDate}{port.points.length ? ` → ${port.points[port.points.length - 1].date}` : ''}</span>}
      </div>

      {basketEmpty && (
        <div className="rounded-md border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-[#161b22] px-3 py-4 text-[12px] text-gray-600 dark:text-gray-400">
          Pick a basket in the selector above to see its performance <span className="font-semibold">since it went live</span>.
          The whole-account curve (all strategies, from the account's first trade) is on the <span className="font-semibold">Portfolio</span> tab.
        </div>
      )}
      {error && <div className="text-sm text-red-500">{error}</div>}
      {loading && !data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-[68px] skeleton rounded-lg" />)}
        </div>
      )}

      {data && (<div className={`space-y-4 transition-opacity duration-200 ${refreshing ? 'opacity-40 pointer-events-none' : ''}`}>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Tile label={`Return · ${isCustom ? 'window' : (RANGE_LABELS[range] || range)}`} value={pct(port.windowReturn)} colored={port.windowReturn} bench={hasSpy ? pct(spy.windowReturn) : undefined} benchLabel={benchLabel} />
          <Tile label="Annualised" value={pct(stats.ann)} colored={stats.ann} bench={hasSpy ? pct(spyStats.ann) : undefined} benchLabel={benchLabel} />
          <Tile label="Volatility" value={pct(stats.vol)} bench={hasSpy ? pct(spyStats.vol) : undefined} benchLabel={benchLabel} />
          <Tile label="Sharpe" value={stats.sharpe == null ? '—' : stats.sharpe.toFixed(2)} colored={stats.sharpe} bench={hasSpy ? (spyStats.sharpe == null ? '—' : spyStats.sharpe.toFixed(2)) : undefined} benchLabel={benchLabel} />
          <Tile label="Max Drawdown" value={pct(stats.maxdd)} colored={stats.maxdd} bench={hasSpy ? pct(spyStats.maxdd) : undefined} benchLabel={benchLabel} />
        </div>

        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4">
          <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Cumulative TWR{spy.points.length ? ` · vs ${benchLabel}` : ''}</div>
          <MiniTwrChart points={port.points} spyPoints={spy.points} height={300} legend />
        </div>

        {/* Commission scenarios — Actual vs Tiered vs At-scale, all ranges + custom. Single-account
            portfolio scope only (commissions are per-account); renders nothing otherwise. Additive +
            read-only — Actual reproduces the graph above; Tiered/At-scale are labelled counterfactuals. */}
        <CommissionScenarioPanel accountId={soloAccountId} range={range}
          customStart={isCustom ? customStart : ''} customEnd={isCustom ? customEnd : ''} asOf={asOf} />

        {/* Dividend receivables / payables — the two-stage lifecycle's operator view (sleeves net of WHT,
            central-book gross payable, reserve residual). Single-account scope; read-only. */}
        <DividendsPanel accountId={soloAccountId} />

        {/* Bucket-layer TWR (pool account): strategies / +reserve / everything (inception→today),
            each the endpoint of the layered cumulative curve; the label shows the bucket's contribution. */}
        {data.layers && data.layers.curves && (() => {
          const lastCum = (a) => (a && a.length ? a[a.length - 1].cumulative : null);
          const s = lastCum(data.layers.curves.strategies);
          const sr = lastCum(data.layers.curves['strategies+reserve']);
          const all = lastCum(data.layers.curves.everything);
          const ppDelta = (b, a) => (b == null || a == null ? '—' : `${b - a >= 0 ? '+' : ''}${((b - a) * 100).toFixed(3)}pp`);
          return (
            <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">TWR by bucket layer · inception→today</div>
              <div className="grid grid-cols-3 gap-3">
                <Tile label="Strategies only" value={pct(s)} colored={s} />
                <Tile label={`+ Reserve · ${ppDelta(sr, s)}`} value={pct(sr)} colored={sr} />
                <Tile label={`Everything · ${ppDelta(all, sr)}`} value={pct(all)} colored={all} />
              </div>
            </div>
          );
        })()}

        {/* Exposure & leverage across all buckets (strategies + reserve + central), point-in-time. */}
        {data.exposure && (
          <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4">
            <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Exposure &amp; leverage · as of {data.exposure.as_of}</div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <Tile label="Long" value={fmtAum(data.exposure.longs)} colored={data.exposure.longs} />
              <Tile label="Short" value={fmtAum(data.exposure.shorts)} colored={data.exposure.shorts} />
              <Tile label="Gross" value={fmtAum(data.exposure.gross_exposure)} />
              <Tile label="Net" value={fmtAum(data.exposure.net_exposure)} colored={data.exposure.net_exposure} />
              <Tile label="Gross Lev" value={data.exposure.gross_leverage == null ? '—' : `${data.exposure.gross_leverage.toFixed(2)}×`} />
              <Tile label="Net Lev" value={data.exposure.net_leverage == null ? '—' : `${data.exposure.net_leverage.toFixed(2)}×`} />
            </div>
          </div>
        )}

        <AttributionPanel
          scope={{
            accountIds: selectedIds && selectedIds.length ? selectedIds : undefined,
            portfolioIds: level === 'basket' && portfolioIds.length ? portfolioIds : undefined,
            strategyIds: level === 'strategy' && strategyIds.length ? strategyIds : undefined,
            start: isCustom ? customStart : (port.anchorDate || undefined),
            end: isCustom ? customEnd : (asOf || undefined),
          }}
          // Headline realized alpha comes from the SAME rebased series as the Return tile +
          // chart (single source of truth), so the panel can never disagree with them.
          realizedPort={port.windowReturn}
          realizedBench={hasSpy ? spy.windowReturn : undefined}
          scopeLabel={level === 'basket' ? 'Basket' : level === 'strategy' ? 'Strategy' : 'Portfolio'}
        />

        <LookthroughPanel scope={{
          accountIds: selectedIds && selectedIds.length ? selectedIds : undefined,
          portfolioIds: level === 'basket' && portfolioIds.length ? portfolioIds : undefined,
          strategyIds: level === 'strategy' && strategyIds.length ? strategyIds : undefined,
        }} />

        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4">
          <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">Per-strategy · per-epoch TWR ({data.epochs?.length || 0})</div>
          {(data.epochs || []).length ? <EpochTable epochs={data.epochs} /> : <p className="text-sm text-gray-400 italic">No strategy epochs in scope.</p>}
        </div>
      </div>)}
    </div>
  );
};

export default TwrPerformancePage;
